import { useState, useEffect, useRef } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { gsap } from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'
import backgroundImage from '../assets/images/bg.jpg'
import { useProjects } from '../hooks/useProjects'
import './Hero.css'

const fallbackSlides = [
  {
    id: 'placeholder-1',
    title: 'Featured Project',
    description: 'Modern digital solutions crafted with precision and performance.',
    imageUrl: '/images/placeholder1.jpg',
  },
]

const inspirationalQuotes = [
  'The only way to do great work is to love what you do. — Steve Jobs',
  'Innovation distinguishes between a leader and a follower. — Steve Jobs',
  'The future belongs to those who believe in the beauty of their dreams. — Eleanor Roosevelt',
]

const profileDescription =
  'I build modern, scalable digital products — from web platforms and applications to custom FiveM game experiences tailored to your vision.'

const MAX_DOT_COUNT = 7

const Hero = () => {
  const [currentQuote, setCurrentQuote] = useState(
    () => inspirationalQuotes[Math.floor(Math.random() * inspirationalQuotes.length)],
  )
  const [slideIndex, setSlideIndex] = useState(0)

  const heroRef = useRef(null)
  const bgRef = useRef(null)
  const contentRef = useRef(null)
  const hintRef = useRef(null)

  const { projects, loading } = useProjects()
  const slides = projects.length > 0
    ? projects.map((p) => ({
        id: p.id,
        title: p.title || p.name,
        description: p.description || 'A curated project from my portfolio.',
        imageUrl: p.imageUrl,
      }))
    : fallbackSlides

  const currentSlide = slides[slideIndex % slides.length]
  const showDotNav = slides.length > 1 && slides.length <= MAX_DOT_COUNT
  const showProgressNav = slides.length > MAX_DOT_COUNT

  useEffect(() => {
    if (slides.length <= 1) return undefined

    const nextIndex = (slideIndex + 1) % slides.length
    const img = new Image()
    img.src = slides[nextIndex].imageUrl
  }, [slideIndex, slides])

  useEffect(() => {
    if (slides.length <= 1) return undefined

    const interval = setInterval(() => {
      setSlideIndex((i) => (i + 1) % slides.length)
    }, 3000)

    return () => clearInterval(interval)
  }, [slides.length])

  useEffect(() => {
    const hero = heroRef.current
    const bg = bgRef.current
    const layout = contentRef.current
    const hint = hintRef.current
    if (!hero || !bg || !layout) return undefined

    const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches
    if (prefersReduced) return undefined

    let mm
    let cancelled = false
    let aboutRevealed = false

    const installTransition = () => {
      const aboutEntry = document.querySelector('#about-entry')
      if (!aboutEntry || cancelled) return false

      mm?.revert()
      mm = gsap.matchMedia()

      mm.add('(min-width: 769px)', () => {
        const crossfade = ScrollTrigger.create({
          trigger: aboutEntry,
          start: 'top bottom',
          end: 'top top',
          scrub: 0.6,
          scroller: document.documentElement,
          invalidateOnRefresh: true,
          onUpdate: (self) => {
            const progress = self.progress

            gsap.set(layout, {
              opacity: 1 - progress,
              y: -40 * progress,
            })

            gsap.set(bg, {
              scale: 1 + progress * 0.08,
              opacity: 1 - progress * 0.4,
            })

            if (hint) {
              gsap.set(hint, {
                opacity: 1 - progress,
                y: progress * 16,
              })
            }

            if (!aboutRevealed && progress > 0.3) {
              aboutRevealed = true
              window.dispatchEvent(new CustomEvent('hero-about-reveal'))
            } else if (aboutRevealed && progress < 0.12) {
              aboutRevealed = false
              window.dispatchEvent(new CustomEvent('hero-about-hide'))
            }
          },
        })

        return () => crossfade.kill()
      })

      return true
    }

    if (!installTransition()) {
      const observer = new MutationObserver(() => {
        if (installTransition()) observer.disconnect()
      })
      observer.observe(document.body, { childList: true, subtree: true })

      return () => {
        cancelled = true
        observer.disconnect()
        mm?.revert()
      }
    }

    const refreshTimer = setTimeout(() => ScrollTrigger.refresh(), 400)

    return () => {
      cancelled = true
      clearTimeout(refreshTimer)
      mm?.revert()
    }
  }, [])

  const handleQuoteClick = () => {
    if (inspirationalQuotes.length <= 1) return

    let nextIndex = Math.floor(Math.random() * inspirationalQuotes.length)
    while (inspirationalQuotes[nextIndex] === currentQuote) {
      nextIndex = Math.floor(Math.random() * inspirationalQuotes.length)
    }
    setCurrentQuote(inspirationalQuotes[nextIndex])
  }

  return (
    <section className="hero-cinematic" id="hero" ref={heroRef}>
      <div
        ref={bgRef}
        className="hero-cinematic-bg"
        style={{ backgroundImage: `url(${backgroundImage})` }}
        aria-hidden="true"
      />
      <div className="hero-cinematic-overlay" aria-hidden="true" />

      <div className="hero-cinematic-layout" ref={contentRef}>
        <div className="hero-cinematic-intro">
          <div className="hero-cinematic-intro-copy">
            <p className="hero-cinematic-eyebrow">Geo's — Portfolio</p>
            <h1 className="cinematic-display hero-cinematic-title">
              <span>Digital craft</span>
              <span className="cinematic-display--muted">beyond the ordinary.</span>
            </h1>
            <p className="hero-cinematic-lead">
              Layer after layer, ideas distilled into systems that unlock new levels of value —
              from modern web apps to immersive game development.
            </p>
          </div>

          <div className="hero-cinematic-intro-visual">
            <img
              src="/images/picture.png"
              alt="Geo"
              className="hero-cinematic-portrait"
              loading="eager"
            />
          </div>
        </div>

        <div className="hero-cinematic-showcase">
          <div className="hero-cinematic-preview">
            {loading ? (
              <div className="hero-cinematic-preview-loading">Loading projects...</div>
            ) : (
              <>
                <AnimatePresence mode="wait">
                  <motion.img
                    key={currentSlide.id}
                    src={currentSlide.imageUrl}
                    alt={currentSlide.title}
                    className="hero-cinematic-preview-img"
                    initial={{ opacity: 0, scale: 1.03 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.99 }}
                    transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                    loading="eager"
                  />
                </AnimatePresence>

                <div className="hero-cinematic-preview-overlay">
                  <AnimatePresence mode="wait">
                    <motion.div
                      key={`meta-${currentSlide.id}`}
                      className="hero-cinematic-preview-meta"
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -8 }}
                      transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
                    >
                      <p className="hero-cinematic-project-index">
                        {String(slideIndex + 1).padStart(2, '0')} / {String(slides.length).padStart(2, '0')}
                      </p>
                      <h2 className="hero-cinematic-project-title">{currentSlide.title}</h2>
                      <p className="hero-cinematic-project-desc">{currentSlide.description}</p>
                    </motion.div>
                  </AnimatePresence>
                </div>

                {showDotNav && (
                  <div className="hero-cinematic-dots" aria-hidden="true">
                    {slides.map((slide, i) => (
                      <button
                        key={slide.id}
                        type="button"
                        className={`hero-cinematic-dot${i === slideIndex ? ' hero-cinematic-dot--active' : ''}`}
                        onClick={() => setSlideIndex(i)}
                        aria-label={`Show project ${i + 1}`}
                      />
                    ))}
                  </div>
                )}

                {showProgressNav && (
                  <div className="hero-cinematic-progress" aria-hidden="true">
                    <div
                      className="hero-cinematic-progress-fill"
                      style={{ width: `${((slideIndex + 1) / slides.length) * 100}%` }}
                    />
                  </div>
                )}
              </>
            )}
          </div>

          <div className="hero-cinematic-side">
            <div className="hero-cinematic-profile">
              <p className="hero-cinematic-name">Geo</p>
              <p className="hero-cinematic-role">Web · Apps · FiveM Development</p>
              <p className="hero-cinematic-desc">{profileDescription}</p>
            </div>

            <div className="hero-cinematic-actions">
              <button type="button" className="classic-btn" onClick={handleQuoteClick}>
                Change Quote
              </button>
              <a href="#showcase" className="classic-btn classic-btn--outline">
                View Projects
              </a>
              <button
                type="button"
                className="hero-cinematic-ai"
                onClick={() => window.dispatchEvent(new CustomEvent('open-ai-chat'))}
              >
                Talk to AI Assistant →
              </button>
            </div>
          </div>
        </div>

        <AnimatePresence mode="wait">
          <motion.blockquote
            key={currentQuote}
            className="hero-cinematic-quote"
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.3 }}
          >
            {currentQuote}
          </motion.blockquote>
        </AnimatePresence>

        <div className="hero-cinematic-hint cinematic-scroll-hint" ref={hintRef}>
          <span>Scroll Down to Discover</span>
          <span className="cinematic-scroll-hint-line" />
        </div>
      </div>
    </section>
  )
}

export default Hero
